function normalizeForMatch(value = "") {
  return value
    .trim()
    .replace(/\s+/g, " ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function buildNormalizedNodeMap(value = "") {
  const startsWithWhitespace = /^\s/.test(value);
  const endsWithWhitespace = /\s$/.test(value);
  let normalized = "";
  const charMap = [];
  let hasStarted = false;
  let pendingSpace = false;
  let pendingSpaceIndex = -1;

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];

    if (/\s/.test(character)) {
      if (!hasStarted) {
        continue;
      }
      pendingSpace = true;
      if (pendingSpaceIndex === -1) {
        pendingSpaceIndex = index;
      }
      continue;
    }

    if (pendingSpace) {
      normalized += " ";
      charMap.push(pendingSpaceIndex);
      pendingSpace = false;
      pendingSpaceIndex = -1;
    }

    const normalizedCharacter = character
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    if (!normalizedCharacter) {
      continue;
    }

    hasStarted = true;
    for (const outputCharacter of normalizedCharacter) {
      normalized += outputCharacter;
      charMap.push(index);
    }
  }

  return {
    normalized,
    charMap,
    startsWithWhitespace,
    endsWithWhitespace,
  };
}

function isWordCharacter(character = "") {
  return /[a-z0-9]/i.test(character);
}

function isWholeWordMatch(value = "", startIndex = 0, length = 0) {
  if (!length) {
    return false;
  }

  const beforeCharacter = startIndex > 0 ? value[startIndex - 1] : "";
  const afterCharacter = value[startIndex + length] || "";

  return !isWordCharacter(beforeCharacter) && !isWordCharacter(afterCharacter);
}

function findWholeWordMatchIndex(value = "", needle = "") {
  if (!value || !needle) {
    return -1;
  }

  let startIndex = value.indexOf(needle);

  while (startIndex !== -1) {
    if (isWholeWordMatch(value, startIndex, needle.length)) {
      return startIndex;
    }

    startIndex = value.indexOf(needle, startIndex + 1);
  }

  return -1;
}

function findWholeWordMatchIndexes(value = "", needle = "") {
  if (!value || !needle) {
    return [];
  }

  const indexes = [];
  let startIndex = value.indexOf(needle);

  while (startIndex !== -1) {
    if (isWholeWordMatch(value, startIndex, needle.length)) {
      indexes.push(startIndex);
    }

    startIndex = value.indexOf(needle, startIndex + 1);
  }

  return indexes;
}

function createHighlightWrapper(doc, term) {
  const tag = term.bold ? "strong" : "span";
  const wrapper = doc.createElement(tag);
  wrapper.setAttribute("data-match-highlight", "true");

  if (!term.highlight) {
    return wrapper;
  }

  if (term.highlight_color) {
    const fontWeight = term.bold ? "font-weight:600;" : "";
    wrapper.setAttribute(
      "style",
      `${fontWeight}background:${term.highlight_color};padding:2px 6px;border-radius:6px;`
    );
    return wrapper;
  }

  wrapper.className = "keyword-highlight";
  return wrapper;
}

function isInsideExistingHighlight(node) {
  let current = node.parentNode;
  while (current) {
    if (
      current.nodeType === 1 &&
      current.getAttribute &&
      current.getAttribute("data-match-highlight") === "true"
    ) {
      return true;
    }
    current = current.parentNode;
  }
  return false;
}

const inlineLikeElementCache = new Map();

function isInlineLikeDisplay(display = "") {
  return (
    display === "contents" ||
    display.startsWith("inline") ||
    display.startsWith("ruby")
  );
}

function resolveDefaultDisplay(tagName = "") {
  if (typeof document === "undefined" || !document.body || !tagName) {
    return "";
  }

  const probeContainer = document.createElement("div");
  probeContainer.setAttribute("aria-hidden", "true");
  probeContainer.style.cssText =
    "position:absolute;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none;";

  const probeElement = document.createElement(tagName);
  probeContainer.appendChild(probeElement);
  document.body.appendChild(probeContainer);

  const { display } = window.getComputedStyle(probeElement);
  probeContainer.remove();

  return display;
}

function isInlineLikeElement(node) {
  if (!node || node.nodeType !== 1 || !node.tagName) {
    return false;
  }

  const tagName = node.tagName.toLowerCase();

  if (!inlineLikeElementCache.has(tagName)) {
    inlineLikeElementCache.set(
      tagName,
      isInlineLikeDisplay(resolveDefaultDisplay(tagName))
    );
  }

  return inlineLikeElementCache.get(tagName);
}

function getClosestNonInlineAncestor(node) {
  let current = node?.parentNode || null;

  while (current) {
    if (current.nodeType === 1 && !isInlineLikeElement(current)) {
      return current;
    }
    current = current.parentNode;
  }

  return null;
}

function hasStructuralSeparatorBetweenNodes(previousNode, currentNode) {
  if (!previousNode || !currentNode || previousNode === currentNode) {
    return false;
  }

  if (getClosestNonInlineAncestor(previousNode) !== getClosestNonInlineAncestor(currentNode)) {
    return true;
  }

  const doc = previousNode.ownerDocument;
  if (!doc?.createRange) {
    return false;
  }

  try {
    const range = doc.createRange();
    range.setStartAfter(previousNode);
    range.setEndBefore(currentNode);

    const fragment = range.cloneContents();
    const walker = doc.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT);
    let currentElement = walker.nextNode();

    while (currentElement) {
      if (!isInlineLikeElement(currentElement)) {
        return true;
      }
      currentElement = walker.nextNode();
    }

    return false;
  } catch (error) {
    return false;
  }
}

function shouldInsertSeparatorBetweenEntries(previousEntry, currentEntry, pendingWhitespaceSeparator) {
  if (!previousEntry) {
    return false;
  }

  if (pendingWhitespaceSeparator) {
    return true;
  }

  if (previousEntry.endsWithWhitespace || currentEntry.startsWithWhitespace) {
    return true;
  }

  return hasStructuralSeparatorBetweenNodes(previousEntry.node, currentEntry.node);
}

function collectTextNodeEntries(root) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const entries = [];
  let currentNode = walker.nextNode();
  let normalizedOffset = 0;
  let normalizedText = "";
  let previousEntry = null;
  let pendingWhitespaceSeparator = false;

  while (currentNode) {
    const value = currentNode.nodeValue || "";
    const {
      normalized,
      charMap,
      startsWithWhitespace,
      endsWithWhitespace,
    } = buildNormalizedNodeMap(value);

    if (normalized) {
      const currentEntry = {
        node: currentNode,
        normalized,
        charMap,
        startsWithWhitespace,
        endsWithWhitespace,
      };

      if (
        shouldInsertSeparatorBetweenEntries(
          previousEntry,
          currentEntry,
          pendingWhitespaceSeparator
        )
      ) {
        normalizedOffset += 1;
        normalizedText += " ";
      }

      entries.push({
        node: currentNode,
        normalized,
        charMap,
        start: normalizedOffset,
        end: normalizedOffset + normalized.length,
        locked: isInsideExistingHighlight(currentNode),
      });
      normalizedOffset += normalized.length;
      normalizedText += normalized;
      previousEntry = currentEntry;
      pendingWhitespaceSeparator = false;
    } else if (/\s/.test(value)) {
      pendingWhitespaceSeparator = true;
    }

    currentNode = walker.nextNode();
  }

  return {
    entries,
    normalizedText,
  };
}

function resolveOccurrenceRange(occurrence, term, normalizedText = "") {
  const expectedNeedle = normalizeForMatch(
    occurrence?.matched_value ?? term?.matched_value ?? term?.value ?? ""
  );

  if (!expectedNeedle) {
    return null;
  }

  const exactSlice = normalizedText.slice(occurrence.start, occurrence.end);
  if (exactSlice === expectedNeedle) {
    return occurrence;
  }

  const candidateStarts = findWholeWordMatchIndexes(normalizedText, expectedNeedle);
  if (!candidateStarts.length) {
    return null;
  }

  let bestStart = candidateStarts[0];
  let bestDistance = Math.abs(bestStart - occurrence.start);

  for (let index = 1; index < candidateStarts.length; index += 1) {
    const candidateStart = candidateStarts[index];
    const candidateDistance = Math.abs(candidateStart - occurrence.start);

    if (candidateDistance < bestDistance) {
      bestStart = candidateStart;
      bestDistance = candidateDistance;
    }
  }

  return {
    ...occurrence,
    start: bestStart,
    end: bestStart + expectedNeedle.length,
  };
}

function wrapNormalizedRangeInNode(entry, occurrence, term) {
  const localStart = Math.max(0, occurrence.start - entry.start);
  const localEnd = Math.min(entry.normalized.length, occurrence.end - entry.start);

  if (localEnd <= localStart) {
    return false;
  }

  const originalStart = entry.charMap[localStart];
  const originalEnd = (entry.charMap[localEnd - 1] ?? originalStart) + 1;

  if (originalStart === undefined || originalEnd <= originalStart) {
    return false;
  }

  const textNode = entry.node;
  const currentNode = originalStart > 0 ? textNode.splitText(originalStart) : textNode;
  if (originalEnd - originalStart < currentNode.nodeValue.length) {
    currentNode.splitText(originalEnd - originalStart);
  }

  const wrapper = createHighlightWrapper(textNode.ownerDocument, term);
  wrapper.textContent = currentNode.nodeValue;
  currentNode.parentNode.replaceChild(wrapper, currentNode);
  return true;
}

function highlightOccurrenceByOffsets(root, occurrence, term) {
  if (
    typeof occurrence.start !== "number" ||
    typeof occurrence.end !== "number" ||
    occurrence.end <= occurrence.start
  ) {
    return false;
  }

  const { entries, normalizedText } = collectTextNodeEntries(root);
  const resolvedOccurrence = resolveOccurrenceRange(occurrence, term, normalizedText);

  if (!resolvedOccurrence) {
    return false;
  }

  const overlaps = entries.filter(
    (entry) => entry.end > resolvedOccurrence.start && entry.start < resolvedOccurrence.end
  );

  if (!overlaps.length || overlaps.some((entry) => entry.locked)) {
    return false;
  }

  let changed = false;
  for (let index = overlaps.length - 1; index >= 0; index -= 1) {
    changed = wrapNormalizedRangeInNode(overlaps[index], resolvedOccurrence, term) || changed;
  }

  return changed;
}

function wrapFirstOccurrenceInNode(textNode, needle, term) {
  const value = textNode.nodeValue || "";
  const { normalized, charMap } = buildNormalizedNodeMap(value);
  const startIndex = findWholeWordMatchIndex(normalized, needle);

  if (startIndex === -1) {
    return false;
  }

  const endIndex = startIndex + needle.length - 1;
  const originalStart = charMap[startIndex];
  const originalEnd = (charMap[endIndex] ?? originalStart) + 1;

  if (originalStart === undefined || originalEnd <= originalStart) {
    return false;
  }

  const currentNode = originalStart > 0 ? textNode.splitText(originalStart) : textNode;
  const afterNodeLength = originalEnd - originalStart;
  if (afterNodeLength < currentNode.nodeValue.length) {
    currentNode.splitText(afterNodeLength);
  }

  const wrapper = createHighlightWrapper(textNode.ownerDocument, term);
  wrapper.textContent = currentNode.nodeValue;
  currentNode.parentNode.replaceChild(wrapper, currentNode);

  return true;
}

function highlightFirstOccurrence(root, term) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) {
          return NodeFilter.FILTER_REJECT;
        }
        if (isInsideExistingHighlight(node)) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  let currentNode = walker.nextNode();
  while (currentNode) {
    if (wrapFirstOccurrenceInNode(currentNode, term.needle, term)) {
      return true;
    }
    currentNode = walker.nextNode();
  }

  return false;
}

function normalizeStructuredOccurrences(term = {}, options = {}) {
  const { fieldName = "" } = options;
  const seen = new Set();

  return (Array.isArray(term?.occurrences) ? term.occurrences : [])
    .map((occurrence) => {
      const start = occurrence?.start;
      const end = occurrence?.end;

      if (typeof start !== "number" || typeof end !== "number" || end <= start) {
        return null;
      }

      const field = `${occurrence?.field ?? ""}`.trim();
      const matchedValue = `${occurrence?.matched_value ?? term?.matched_value ?? term?.value ?? ""}`.trim();

      if (!matchedValue) {
        return null;
      }

      if (fieldName && field !== fieldName) {
        return null;
      }

      return {
        field,
        matched_value: matchedValue,
        start,
        end,
      };
    })
    .filter(Boolean)
    .filter((occurrence) => {
      const key = [
        occurrence.field,
        occurrence.start,
        occurrence.end,
        normalizeForMatch(occurrence.matched_value),
      ].join(":");

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .sort((left, right) => left.start - right.start || left.end - right.end);
}

function normalizeLegacyOccurrence(term = {}, options = {}) {
  const { fieldName = "" } = options;
  const start = term?.start;
  const end = term?.end;

  if (typeof start !== "number" || typeof end !== "number" || end <= start) {
    return null;
  }

  const field = `${term?.field ?? ""}`.trim();
  if (fieldName && field && field !== fieldName) {
    return null;
  }

  const matchedValue = `${term?.matched_value ?? term?.value ?? ""}`.trim();
  if (!matchedValue) {
    return null;
  }

  return {
    field,
    matched_value: matchedValue,
    start,
    end,
  };
}

function buildPreciseTerms(displayTerms = [], options = {}) {
  const {
    fieldName = "",
    allowedTargetFields = null,
  } = options;
  const seen = new Set();

  return displayTerms
    .filter((term) => {
      if (!term) return false;
      if (!term.highlight && !term.bold) return false;
      if (!allowedTargetFields || !term.target_field) return true;
      return allowedTargetFields.includes(term.target_field);
    })
    .map((term) => {
      const matchValue = term.matched_value || term.value || "";
      const hasStructuredOccurrences =
        Array.isArray(term?.occurrences) && term.occurrences.length > 0;
      const structuredOccurrences = normalizeStructuredOccurrences(term, { fieldName });
      const legacyOccurrence = hasStructuredOccurrences
        ? null
        : normalizeLegacyOccurrence(
            {
              ...term,
              matched_value: matchValue,
            },
            { fieldName }
          );

      return {
        ...term,
        needle: normalizeForMatch(matchValue),
        occurrences: structuredOccurrences.length
          ? structuredOccurrences
          : legacyOccurrence
            ? [legacyOccurrence]
            : [],
        hasStructuredOccurrences,
      };
    })
    .filter((term) => {
      if (!term.needle) {
        return false;
      }

      if (term.hasStructuredOccurrences && fieldName && !term.occurrences.length) {
        return false;
      }

      if (!term.hasStructuredOccurrences && fieldName && term.field && term.field !== fieldName) {
        return false;
      }

      return true;
    })
    .sort((left, right) => right.needle.length - left.needle.length)
    .filter((term) => {
      const key = `${term.target_field || ""}:${term.needle}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

function normalizeKeywordList(keywordList = []) {
  const seen = new Set();

  return (Array.isArray(keywordList) ? keywordList : [])
    .map((keyword) => (typeof keyword === "string" ? keyword.trim() : ""))
    .filter(Boolean)
    .filter((keyword) => {
      const normalized = normalizeForMatch(keyword);
      if (!normalized || seen.has(normalized)) {
        return false;
      }
      seen.add(normalized);
      return true;
    });
}

function normalizeDisplayTerms(displayTerms = []) {
  const seen = new Set();

  return (Array.isArray(displayTerms) ? displayTerms : [])
    .filter(Boolean)
    .map((term) => {
      const matchedValue = `${term?.matched_value ?? term?.value ?? ""}`.trim();
      const highlight = term?.highlight ?? true;
      const bold = term?.bold ?? true;

      return {
        ...term,
        matched_value: matchedValue,
        value: `${term?.value ?? matchedValue}`.trim(),
        highlight,
        bold,
        occurrences: Array.isArray(term?.occurrences) ? term.occurrences.filter(Boolean) : [],
      };
    })
    .filter((term) => term.matched_value && (term.highlight || term.bold))
    .filter((term) => {
      const key = `${term.target_field || ""}:${normalizeForMatch(term.matched_value)}`;
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

export function resolveHighlightPayload(payload = {}) {
  const displayTerms = normalizeDisplayTerms(payload?.display_terms);
  const keywordList = normalizeKeywordList(payload?.keyword_list);

  if (displayTerms.length) {
    return {
      mode: "display_terms",
      displayTerms,
      keywordList,
    };
  }

  if (keywordList.length) {
    return {
      mode: "keyword_list",
      displayTerms: [],
      keywordList,
    };
  }

  return {
    mode: "none",
    displayTerms: [],
    keywordList: [],
  };
}

export function resolveLegacyHighlightKeywords(payload = {}) {
  const keywordList = normalizeKeywordList(payload?.keyword_list);
  if (keywordList.length) {
    return keywordList;
  }

  return normalizeDisplayTerms(payload?.display_terms).map(
    (term) => term.matched_value || term.value
  );
}

export function highlightKeywordsInHTML(html, keywords = []) {
  if (!html || !keywords.length) return html;

  const escapedKeywords = keywords
    .filter(Boolean)
    .map((keyword) => keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  if (!escapedKeywords.length) return html;

  const regex = new RegExp(
    `(^|[^\\p{L}\\p{N}])(${escapedKeywords.join("|")})(?=$|[^\\p{L}\\p{N}])`,
    "giu"
  );

  return html.replace(regex, `$1<strong class="keyword-highlight">$2</strong>`);
}

export function highlightKeywordsRichHTML(html, displayTerms = [], options = {}) {
  if (!html || !displayTerms.length) return html;

  const preciseTerms = buildPreciseTerms(displayTerms, options);

  if (!preciseTerms.length) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  let hasChanges = false;

  for (const term of preciseTerms) {
    let usedExactOccurrence = false;

    for (const occurrence of term.occurrences) {
      usedExactOccurrence =
        highlightOccurrenceByOffsets(doc.body, occurrence, term) || usedExactOccurrence;
    }

    hasChanges =
      usedExactOccurrence ||
      ((!term.hasStructuredOccurrences || !usedExactOccurrence) &&
        highlightFirstOccurrence(doc.body, term)) ||
      hasChanges;
  }

  return hasChanges ? doc.body.innerHTML : html;
}

export function highlightPreferredHTML(html, payload = {}, options = {}) {
  const { mode, displayTerms, keywordList } = resolveHighlightPayload(payload);

  if (mode === "display_terms") {
    return highlightKeywordsRichHTML(html, displayTerms, options);
  }

  if (mode === "keyword_list") {
    return highlightKeywordsInHTML(html, keywordList);
  }

  return html;
}
